import { match, P } from 'ts-pattern';
import { cleanRefName } from './helpers/schema';
import type {
  DereferencedParsedSchema,
  ParsedMethod,
  ParsedObjectProperty,
  ParsedPackage,
  ParsedPolymorphProperties,
  ParsedSchema,
  ParsedSchemaWithRef,
  ParsedService,
  ParsedSource,
} from './types/parsed';

const CIRCULAR_SYMBOL = Symbol('__circular');

function getCircularPlaceholder(ref: string) {
  return Object.freeze({ [CIRCULAR_SYMBOL]: ref } as unknown as DereferencedParsedSchema);
}

export function dereferenceObjectProperties(
  properties: Map<string, ParsedObjectProperty>,
  schemas: Map<string, ParsedSchema>,
  visited: Map<string, DereferencedParsedSchema> = new Map(),
) {
  const dereferencedProperties = new Map<string, ParsedObjectProperty<DereferencedParsedSchema>>();

  for (const [key, property] of properties) {
    const dereferencedSchema = dereferenceSchema(property.schema, schemas, visited);

    if (dereferencedSchema) {
      property.schema = dereferencedSchema;
      dereferencedProperties.set(key, property as ParsedObjectProperty<DereferencedParsedSchema>);
    }
  }

  const circularDepthMap = new Map<string, number>();

  function replaceCircularProperties(schema: DereferencedParsedSchema): DereferencedParsedSchema {
    if (CIRCULAR_SYMBOL in schema) {
      const ref = (schema as any)[CIRCULAR_SYMBOL] as string;
      const currentDepth = circularDepthMap.get(ref) || 0;
      const refSchema = visited.get(ref);

      if (refSchema && currentDepth < 3) {
        circularDepthMap.set(ref, currentDepth + 1);

        return match(refSchema)
          .with({ object: P.not(P.nullish) }, (s) => ({ ...s, object: { ...s.object, properties: new Map() } }))
          .with({ oneOf: P.not(P.nullish) }, (s) => ({ ...s, oneOf: { ...s.oneOf, properties: new Map() } }))
          .with({ polymorph: P.not(P.nullish) }, (s) => ({ ...s, polymorph: { ...s.polymorph, properties: new Map() } }))
          .otherwise((s) => s);
      }
    }

    if ('array' in schema && CIRCULAR_SYMBOL in schema.array.itemSchema) {
      return {
        ...schema,
        array: {
          ...schema.array,
          itemSchema: replaceCircularProperties(schema.array.itemSchema),
        },
      };
    }

    return schema;
  }

  for (const [_, property] of dereferencedProperties) {
    property.schema = replaceCircularProperties(property.schema);
  }

  return dereferencedProperties;
}

export function dereferencePolymorphProperties(
  properties: ParsedPolymorphProperties,
  schemas: Map<string, ParsedSchema>,
  visited: Map<string, DereferencedParsedSchema> = new Map(),
) {
  const dereferencedProperties: ParsedPolymorphProperties<DereferencedParsedSchema> = new Map();

  for (const [k, v] of properties) {
    dereferencedProperties.set(k, dereferenceObjectProperties(v, schemas, visited));
  }

  return dereferencedProperties;
}

export function dereferenceSchema(
  schema: ParsedSchemaWithRef,
  schemas: Map<string, ParsedSchema>,
  visited: Map<string, DereferencedParsedSchema> = new Map(),
): DereferencedParsedSchema | undefined {
  return match(schema)
    .returnType<DereferencedParsedSchema | undefined>()
    .with({ $ref: P.not(P.nullish) }, (s) => {
      const fullGrpcName = cleanRefName(s);
      const refSchema = schemas.get(fullGrpcName);

      if (!refSchema) {
        console.error(`schema for ref ${fullGrpcName} not found, dereferencing failed`);
        return undefined;
      }

      if (visited.has(fullGrpcName)) {
        return visited.get(fullGrpcName)!;
      }

      // Insert placeholder to mark "in-progress"
      visited.set(fullGrpcName, getCircularPlaceholder(fullGrpcName));

      const dereferenced = dereferenceSchema(refSchema, schemas, visited);

      if (dereferenced) {
        visited.set(fullGrpcName, dereferenced);
        return dereferenced;
      }

      visited.delete(fullGrpcName);
      return undefined;
    })
    .with({ array: P.not(P.nullish) }, (s) => {
      const dereferencedItemSchema = dereferenceSchema(s.array.itemSchema, schemas, visited);

      if (!dereferencedItemSchema) {
        return undefined;
      }

      return {
        ...s,
        array: {
          ...s.array,
          itemSchema: dereferencedItemSchema,
        },
      };
    })
    .with({ map: P.not(P.nullish) }, (s) => {
      const keySchema = dereferenceSchema(s.map.keySchema, schemas, visited);
      const itemSchema = dereferenceSchema(s.map.itemSchema, schemas, visited);

      if (!keySchema || !itemSchema) {
        return undefined;
      }

      return {
        ...s,
        map: {
          ...s.map,
          keySchema,
          itemSchema,
        },
      };
    })
    .with({ oneOf: P.not(P.nullish) }, (s) => {
      return {
        ...s,
        oneOf: {
          ...s.oneOf,
          properties: dereferenceObjectProperties(s.oneOf.properties, schemas, visited),
        },
      };
    })
    .with({ object: P.not(P.nullish) }, (s) => {
      return {
        ...s,
        object: {
          ...s.object,
          properties: dereferenceObjectProperties(s.object.properties, schemas, visited),
        },
      };
    })
    .with({ polymorph: { properties: P.not(P.nullish) } }, (s) => {
      return {
        ...s,
        polymorph: {
          ...s.polymorph,
          properties: dereferencePolymorphProperties(s.polymorph.properties, schemas, visited),
        },
      };
    })
    .otherwise((s) => s as DereferencedParsedSchema);
}

export function dereferenceMethod(
  method: ParsedMethod,
  dereferencedSchemas: Map<string, DereferencedParsedSchema>,
  visitedPackages = new Map<string, ParsedPackage<DereferencedParsedSchema>>(),
  visitedServices = new Map<string, ParsedService<DereferencedParsedSchema>>(),
  visited = new Map<string, ParsedMethod<DereferencedParsedSchema>>(),
): ParsedMethod<DereferencedParsedSchema> {
  if (visited.has(method.fullGrpcName)) {
    return visited.get(method.fullGrpcName) as ParsedMethod<DereferencedParsedSchema>;
  }

  visited.set(method.fullGrpcName, method as ParsedMethod<DereferencedParsedSchema>);

  const visitedSchemas = new Map<string, DereferencedParsedSchema>();

  const dereferenced: ParsedMethod<DereferencedParsedSchema> = {
    ...method,
    requestBody: method.requestBody ? dereferenceSchema(method.requestBody, dereferencedSchemas, visitedSchemas) : undefined,
    responseBody: method.responseBody ? dereferenceSchema(method.responseBody, dereferencedSchemas, visitedSchemas) : undefined,
    pathParameters: method.pathParameters ? dereferenceObjectProperties(method.pathParameters, dereferencedSchemas, visitedSchemas) : undefined,
    queryParameters: method.queryParameters ? dereferenceObjectProperties(method.queryParameters, dereferencedSchemas, visitedSchemas) : undefined,
    rootEntitySchema: method.rootEntitySchema ? dereferenceSchema(method.rootEntitySchema, dereferencedSchemas, visitedSchemas) : undefined,
    parentService: dereferenceService(method.parentService, dereferencedSchemas, visitedPackages, visitedServices),
  };

  // Store the fully dereferenced method result.
  visited.set(method.fullGrpcName, dereferenced);

  return dereferenced;
}

export function dereferenceService(
  service: ParsedService,
  dereferencedSchemas: Map<string, DereferencedParsedSchema>,
  visitedPackages = new Map<string, ParsedPackage<DereferencedParsedSchema>>(),
  visited = new Map<string, ParsedService<DereferencedParsedSchema>>(),
): ParsedService<DereferencedParsedSchema> {
  if (visited.has(service.name)) {
    return visited.get(service.name) as ParsedService<DereferencedParsedSchema>;
  }

  // Mark service as processing to avoid cycles
  visited.set(service.name, service as ParsedService<DereferencedParsedSchema>);

  const visitedMethods = new Map<string, ParsedMethod<DereferencedParsedSchema>>();

  const dereferenced: ParsedService<DereferencedParsedSchema> = {
    ...service,
    methods: service.methods.map((method) => dereferenceMethod(method, dereferencedSchemas, visitedPackages, visited, visitedMethods)),
    parentPackage: dereferencePackage(service.parentPackage, dereferencedSchemas, visitedPackages),
  };

  visited.set(service.name, dereferenced);
  return dereferenced;
}

export function dereferencePackage(
  pkg: ParsedPackage,
  dereferencedSchemas: Map<string, DereferencedParsedSchema>,
  visited = new Map<string, ParsedPackage<DereferencedParsedSchema>>(),
): ParsedPackage<DereferencedParsedSchema> {
  if (visited.has(pkg.name)) {
    return visited.get(pkg.name) as ParsedPackage<DereferencedParsedSchema>;
  }

  // Mark package as being processed to avoid infinite recursion
  visited.set(pkg.name, pkg as ParsedPackage<DereferencedParsedSchema>);

  const visitedServices = new Map<string, ParsedService<DereferencedParsedSchema>>();

  const dereferenced: ParsedPackage<DereferencedParsedSchema> = {
    ...pkg,
    services: pkg.services.map((service) => dereferenceService(service, dereferencedSchemas, visited, visitedServices)),
  };

  visited.set(pkg.name, dereferenced);

  return dereferenced;
}

export function dereferenceSource(source: ParsedSource): ParsedSource<DereferencedParsedSchema, DereferencedParsedSchema> {
  const dereferencedSchemas = new Map<string, DereferencedParsedSchema>();

  const visitedSchemas = new Map<string, DereferencedParsedSchema>();
  for (const [schemaName, schema] of source.schemas) {
    const dereferencedSchema = dereferenceSchema(schema, source.schemas, visitedSchemas);
    if (dereferencedSchema) {
      visitedSchemas.set(schemaName, dereferencedSchema);
      dereferencedSchemas.set(schemaName, dereferencedSchema);
    }
  }

  const visitedPackages = new Map<string, ParsedPackage<DereferencedParsedSchema>>();

  return {
    ...source,
    schemas: dereferencedSchemas,
    packages: source.packages.map((pkg) => dereferencePackage(pkg, dereferencedSchemas, visitedPackages)),
  };
}
